import React from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate, Outlet,
  Route,
  RouterProvider
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorBoundary from './components/Common/ErrorBoundary';
import AppComponent from "./layout/AppComponent";
import NotFoundPage from './pages/NotFoundPage';
import usePlatform from './hooks/usePlatform';
import {
  AdminHome,
  AlarmExtraction,
  Alarms,
  ApplicationEditorRoot,
  Availability,
  Clusters,
  ClusterVersion,
  ChannelHistoryStatistics,
  ChannelMonthHistoryStatistics,
  ChannelStatistics,
  ClientApplicationSessionStatistics,
  ClientSessionStatistics,
  ClientSynopticSessionsStatistics,
  Dashboards,
  DashboardsRoot,
  DataExportStatusHome,
  DataExportStatusInflux,
  DataExportStatusNxcals,
  DeviceStatistics,
  DeviceStatusReporting,
  DeviceStatusTimeline,
  Heatmap,
  Hyetogram,
  MeasurementsStatistics,
  MeteoDataExtraction,
  MonitoringGroups,
  MSDAChannelStatistics,
  MSDashboards,
  MSDashboardsRoot,
  MSHistoryStatistics,
  MSMonthHistoryStatistics,
  MSStatistics,
  NetAmbientDoses,
  NewDashboard,
  NewNotification,
  Notification,
  NotificationList,
  Pipeline,
  PipelineRoot,
  PlatformRoot,
  RemusApplication,
  RemusEventStatistics,
  ReportingHome,
  Snapshots,
  StatisticsHome,
  SynopticsCatalog,
  SynopticWidgetsCatalog,
  SystemNxcals,
  SystemNxcalsByChid,
  SystemNxcalsByType,
  SystemStats,
  Trends,
  UnsubscribeNotifications,
  UserPrivilegesStatistics,
  DeviceMonthHistoryStatistics,
  LatestParametersStatistics,
  ParametersHistoryStatistics
} from './routes';

import {initSelectors} from './behavior/init';
import useUserQuery from './behavior/init/hooks/useUserQuery';
import platforms, {getAppPlatforms} from './platformConfig';
import {isAppMode} from './config/mockAuth';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './style.css';
import LoadingComponent from './LoadingComponent';
import './mobile.less';
import lazyWithRetry from './utils/lazyWithRetry';
import { registerAppRouter } from './utils/navigation';
import DeviceStatusProvider from './behavior/deviceStatus/DeviceStatusContext';
// Page components are lazy-loaded so each route ships its own chunk and the initial
// bundle stays small. The shared <Suspense> boundary lives in layout/AppComponent.jsx.
// lazyWithRetry reloads once on a stale dynamic-import failure (Vite re-optimize race
// in dev, stale hashed chunks after a deploy in prod).
const UserPrivilegesStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/UserPrivilegesStatisticsPage'));
const ClientSessionStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/ClientSessionStatisticsPage'));
const ClientApplicationSessionStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/ClientApplicationSessionStatisticsPage'));
const ClientSynopticSessionStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/ClientSynopticSessionStatisticsPage'));
const HyetogramPage = lazyWithRetry(() => import('./pages/HyetogramPage'));
const MeteoDataExtractionPage = lazyWithRetry(() => import('./pages/MeteoDataExtractionPage'));
const NetAmbientDosesPage = lazyWithRetry(() => import('./pages/NetAmbientDosesPage'));
const SystemNxcalsByTypePage = lazyWithRetry(() => import('./pages/SystemNxcalsPage/SystemNxcalsByTypePage'));
const SystemNxcalsByChidPage = lazyWithRetry(() => import('./pages/SystemNxcalsPage/SystemNxcalsByChidPage'));
const SystemNxcalsPage = lazyWithRetry(() => import('./pages/SystemNxcalsPage/SystemNxcalsPage'));
const HeatmapPage = lazyWithRetry(() => import('./pages/HeatmapPage'));
const SystemStatsPage = lazyWithRetry(() => import('./pages/SystemStatsPage'));
const DataExportStatusHomePage = lazyWithRetry(() => import('./pages/DataExportStatus/DataExportStatusHomePage'));
const DataExportStatusNxcalsPage = lazyWithRetry(() => import('./pages/DataExportStatus/DataExportStatusNxcalsPage'));
const DataExportStatusInfluxPage = lazyWithRetry(() => import('./pages/DataExportStatus/DataExportStatusInfluxPage'));
const ApplicationEditorPage = lazyWithRetry(() => import('./pages/ApplicationEditorPage'));
const SynopticWidgetsCatalogPage = lazyWithRetry(() => import('./pages/SynopticWidgetsCatalogPage'));
const SynopticsCatalogPage = lazyWithRetry(() => import('./pages/SynopticsCatalogPage'));
const RemusApplicationPage = lazyWithRetry(() => import('./pages/RemusApplicationPage'));
const DashboardsPage = lazyWithRetry(() => import('./pages/DashboardsPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const MSDashboardsPage = lazyWithRetry(() => import('./pages/MSDashboardsPage'));
const NotificationListPage = lazyWithRetry(() => import('./pages/NotificationPage/NotificationListPage'));
const UnsubscribeNotificationsPage = lazyWithRetry(() => import('./pages/NotificationPage/UnsubscribeNotifications'));
const NotificationEditPage = lazyWithRetry(() => import('./pages/NotificationPage/NotificationEditPage'));
const ReportingPage = lazyWithRetry(() => import('./pages/ReportingPage'));
const SnapshotsPage = lazyWithRetry(() => import('./pages/SnapshotsPage'));
const AlarmExtractionPage = lazyWithRetry(() => import('./pages/AlarmExtractionPage'));
const AvailabilityPage = lazyWithRetry(() => import('./pages/AvailabilityPage'));
const MeasurementsStatisticsPage = lazyWithRetry(() => import('./pages/MeasurementsStatisticsPage'));
const DeviceStatusReportingPage = lazyWithRetry(() => import('./pages/DeviceStatusReporting'));
const DeviceTimelinePage = lazyWithRetry(() => import('./pages/DeviceStatusReporting/DeviceTimelinePage'));
const PipelinePage = lazyWithRetry(() => import('./pages/PipelinePage/PipelinePage'));
const StatisticsHomePage = lazyWithRetry(() => import('./pages/Statistics/StatisticsHomePage'));
const ChannelStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/ChannelStatisticsPage'));
const MSStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/MSStatisticsPage'));
const DeviceStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/DeviceStatisticsPage'));
const RemusEventStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/RemusEventStatisticsPage'));
const ChannelHistoryStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/ChannelHistoryStatisticsPage'));
const MSHistoryStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/MSHistoryStatisticsPage'));
const ChannelMonthHistoryStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/ChannelMonthHistoryStatisticsPage'));
const MSMonthHistoryStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/MSMonthHistoryStatisticsPage'));
const MSDAChannelStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/MSDAChannelStatisticsPage'));
const AlarmsPage = lazyWithRetry(() => import('./pages/AlarmsPage'));
const MonitoringGroupsPage = lazyWithRetry(() => import('./pages/MonitoringGroupsPage'));
const TrendsPage = lazyWithRetry(() => import('./pages/TrendsPage'));
const HomePage = lazyWithRetry(() => import('./pages/HomePage'));
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage'));
const DeviceMonthHistoryStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/DeviceMonthHistoryStatisticsPage'));
const LatestParametersStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/LatestParametersStatisticsPage'));
const ParametersHistoryStatisticsPage = lazyWithRetry(() => import('./pages/Statistics/ParametersHistoryStatisticsPage'));

const BackendUnreachable = ({ onRetry }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      bgcolor: 'var(--ds-bg)',
    }}
  >
    <Paper
      variant="outlined"
      sx={{
        maxWidth: 420,
        width: '100%',
        p: { xs: 2, sm: 3 },
        borderRadius: 1,
      }}
    >
      <Stack spacing={1.5} alignItems="flex-start">
        <Stack direction="row" spacing={1} alignItems="center">
          <CloudOffIcon color="error" sx={{ fontSize: 22 }} />
          <Typography variant="subtitle1" component="h1" fontWeight={600}>
            Backend unreachable
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          We couldn&apos;t reach the REMUS server. It may be restarting or
          temporarily unavailable. Please try again in a moment.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
        >
          Retry
        </Button>
      </Stack>
    </Paper>
  </Box>
);

BackendUnreachable.propTypes = {
  onRetry: PropTypes.func.isRequired,
};

const App = () => {
  // Boot fetch: the ['user'] query auto-runs on mount, replacing the old START
  // dispatch. `isLoading` is true only during the initial single attempt (retry is
  // disabled on this query), so gating matches the old init reducer exactly.
  const { data: user, isLoading } = useUserQuery();

  if (isLoading) {
    return <LoadingComponent />;
  }
  if (!user) {
    // The user query fails to populate `user` when the backend is down or still
    // booting. Without this guard, the router below would crash on
    // `user.authorities`. Render a clear message instead.
    return <BackendUnreachable onRetry={() => window.location.reload()} />;
  }
  if (!platforms) {
    return <h1>Error</h1>;
  }
  if (Object.keys(platforms).length === 0) {
    return <h1>No Platforms</h1>;
  }

  // The desktop (Electron) build now shows the same full app as the web build;
  // the Sidebar tucks the non-essential entries behind "Show more" so it stays
  // simple on first glance. The only routing differences are below: the app
  // lands on Trends instead of Home and never exposes the Home page.
  const isApp = isAppMode();
  const landingPlatform = isApp ? getAppPlatforms()[0] : platforms[0];

  function RequireRole({ userRoles, requiredRoles, children }) {
    if (!requiredRoles || requiredRoles.length === 0 || userRoles.some(r => requiredRoles.includes(r))){
      return children;
    }
    return <Navigate to="/not-authorized" replace />;
  }

  RequireRole.propTypes = {
    userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
    requiredRoles: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.node.isRequired,
  };

  function AdminRoleGuard({ userRoles }) {
    return (
        <RequireRole userRoles={userRoles} requiredRoles={[initSelectors.ROLES.ROLE_ADMIN]}>
          <Outlet />
        </RequireRole>
    );
  }

  AdminRoleGuard.propTypes = {
    userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  };

  function ExpertRoleGuard({ userRoles }) {
    return (
        <RequireRole
            userRoles={userRoles}
            requiredRoles={[
              initSelectors.ROLES.ROLE_ADMIN,
              initSelectors.ROLES.ROLE_EXPERT_EN,
              initSelectors.ROLES.ROLE_EXPERT_RP
            ]}
        >
          <Outlet />
        </RequireRole>
    );
  }

  ExpertRoleGuard.propTypes = {
    userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  };

  const PlatformLayout = () => (usePlatform() ? <AppComponent /> : <NotFoundPage />);

  const router = createBrowserRouter(createRoutesFromElements(
      <Route path="/" errorElement={<ErrorBoundary />}>
        <Route path="/:platform" element={<PlatformLayout />}>
          <Route path={AdminHome.getRoute(false, false)} handle={{ crumb: "Admin" }} element={<AdminRoleGuard userRoles={user.authorities} />}>
            <Route index element={<AdminPage />} />
            <Route path={SystemStats.getRoute()} handle={{ crumb: "System Statistics" }} element={<SystemStatsPage />} />
            <Route path={Heatmap.getRoute()} handle={{ crumb: "Processes Heatmap" }} element={<HeatmapPage />} />
            <Route path={SystemNxcals.getRoute(false, false)} handle={{ crumb: "NXCALS Publication Management" }}>
              <Route index element={<SystemNxcalsPage />} />
              <Route path={SystemNxcalsByChid.getRoute()} handle={{ crumb: "NXCALS pub. by Channel Id" }} element={<SystemNxcalsByChidPage />} />
              <Route path={SystemNxcalsByType.getRoute()} handle={{ crumb: "NXCALS pub. by Type" }} element={<SystemNxcalsByTypePage />} />
            </Route>
            <Route path={DataExportStatusHome.getRoute(false, false)} handle={{ crumb: "Data Export" }}>
              <Route index element={<DataExportStatusHomePage />} />
              <Route path={DataExportStatusNxcals.getRoute()} handle={{ crumb: "NXCALS" }} element={<DataExportStatusNxcalsPage />} />
              <Route path={DataExportStatusInflux.getRoute()} handle={{ crumb: "InfluxDB" }} element={<DataExportStatusInfluxPage />} />
            </Route>
          </Route>

          <Route path={ApplicationEditorRoot.getRoute(false, false)} handle={{ crumb: "Application Editor" }}>
            <Route index element={<ApplicationEditorPage />} />
            <Route path={SynopticWidgetsCatalog.getRoute()} handle={{ crumb: "Manage Widgets" }} element={<SynopticWidgetsCatalogPage />} />
            <Route path={SynopticsCatalog.getRoute()} handle={{ crumb: "Manage Synoptics Backgrounds" }} element={<SynopticsCatalogPage />} />
            <Route path={RemusApplication.getRoute()} handle={{ crumb: "Edit" }} element={<RemusApplicationPage />} />
          </Route>

          <Route path={DashboardsRoot.getRoute(false, false)} handle={{ crumb: "Dashboards" }}>
            <Route index element={<DashboardsPage />} />
            <Route path={NewDashboard.getRoute()} handle={{ crumb: "New" }} element={<DashboardPage />} />
            <Route path={Dashboards.getRoute()} handle={{ crumb: "Dashboard" }} element={<DashboardPage />} />
            <Route path={MSDashboardsRoot.getRoute(false, false)}>
              <Route index element={<DashboardsPage />} />
              <Route path={MSDashboards.getRoute()} handle={{ crumb: "MS Dashboards" }} element={<MSDashboardsPage />} />
            </Route>
          </Route>

          <Route path={NotificationList.getRoute(false, false)} handle={{ crumb: "Notifications" }}>
            <Route index element={<NotificationListPage />} />
            <Route path={UnsubscribeNotifications.getRoute()} handle={{ crumb: "Unsubscribe" }} element={<UnsubscribeNotificationsPage />} />
            <Route path={NewNotification.getRoute()} handle={{ crumb: "New" }} element={<NotificationEditPage />} />
            <Route path={Notification.getRoute()} handle={{ crumb: "Edit" }} element={<NotificationEditPage />} />
          </Route>

          <Route path={ReportingHome.getRoute(false, false)} handle={{ crumb: "Reporting" }}>
            <Route index element={<ReportingPage />} />
            <Route path={Snapshots.getRoute()} handle={{ crumb: "Snapshots Report" }} element={<SnapshotsPage />} />
            <Route path={AlarmExtraction.getRoute()} handle={{ crumb: "Alarm Extraction" }} element={<AlarmExtractionPage />} />
            <Route path={Availability.getRoute()} handle={{ crumb: "Availability" }} element={<AvailabilityPage />} />
            <Route path={Hyetogram.getRoute()} handle={{ crumb: "Hyetogram" }}
                   element={
                     <RequireRole userRoles={user.authorities} requiredRoles={[initSelectors.ROLES.ROLE_ADMIN, initSelectors.ROLES.ROLE_EXPERT_EN, initSelectors.ROLES.ROLE_EXPERT_RP]}>
                       <HyetogramPage />
                     </RequireRole>
                   }
            />
            <Route path={MeteoDataExtraction.getRoute()} handle={{ crumb: "Meteo Data Extraction" }}
                   element={
                     <RequireRole userRoles={user.authorities} requiredRoles={[initSelectors.ROLES.ROLE_ADMIN, initSelectors.ROLES.ROLE_EXPERT_EN, initSelectors.ROLES.ROLE_EXPERT_RP]}>
                       <MeteoDataExtractionPage />
                     </RequireRole>
                   }
            />
            <Route path={MeasurementsStatistics.getRoute()} handle={{ crumb: "Measurements Statistics" }} element={<MeasurementsStatisticsPage />} />
            <Route path={DeviceStatusReporting.getRoute(false, false)} handle={{ crumb: "Device Status" }} element={<DeviceStatusProvider />}>
              <Route index element={<DeviceStatusReportingPage />} />
              <Route path={DeviceStatusTimeline.getRoute()} handle={{ crumb: "Device Timeline" }} element={<DeviceTimelinePage />} />
            </Route>
            <Route path={NetAmbientDoses.getRoute(false, false)} handle={{ crumb: "Measurements Processing" }} element={<ExpertRoleGuard userRoles={user.authorities} />}>
              <Route index element={<NetAmbientDosesPage />} />
              <Route path={`${Clusters.getRoute()}/${ClusterVersion.getRoute()}/${PipelineRoot.getRoute()}/${Pipeline.getRoute()}`} handle={{ crumb: "Pipeline" }} element={<PipelinePage />} />
            </Route>
          </Route>

          <Route path={StatisticsHome.getRoute(false, false)} handle={{ crumb: "Statistics" }}>
            <Route index element={<StatisticsHomePage />} />
            <Route path={ChannelStatistics.getRoute()} handle={{ crumb: "Channel" }} element={<ChannelStatisticsPage />} />
            <Route path={MSStatistics.getRoute()} handle={{ crumb: "MS" }} element={<MSStatisticsPage />} />
            <Route path={DeviceStatistics.getRoute()} handle={{ crumb: "Device" }} element={<DeviceStatisticsPage />} />
            <Route path={RemusEventStatistics.getRoute()} handle={{ crumb: "Events" }} element={<RemusEventStatisticsPage />} />
            <Route path={ChannelHistoryStatistics.getRoute()} handle={{ crumb: "Channel History" }} element={<ChannelHistoryStatisticsPage />} />
            <Route path={MSHistoryStatistics.getRoute()} handle={{ crumb: "MS History" }} element={<MSHistoryStatisticsPage />} />
            <Route path={ChannelMonthHistoryStatistics.getRoute()} handle={{ crumb: "Channel Monthly History" }} element={<ChannelMonthHistoryStatisticsPage />} />
            <Route path={DeviceMonthHistoryStatistics.getRoute()} handle={{ crumb: "Device Monthly History" }} element={<DeviceMonthHistoryStatisticsPage />} />
            <Route path={MSMonthHistoryStatistics.getRoute()} handle={{ crumb: "MS Monthly History" }} element={<MSMonthHistoryStatisticsPage />} />
            <Route path={UserPrivilegesStatistics.getRoute()} handle={{ crumb: "User Privileges" }}
                   element={
                     <RequireRole userRoles={user.authorities} requiredRoles={[initSelectors.ROLES.ROLE_ADMIN, initSelectors.ROLES.ROLE_EXPERT_EN, initSelectors.ROLES.ROLE_EXPERT_RP]}>
                       <UserPrivilegesStatisticsPage />
                     </RequireRole>
                   }
            />
            <Route path={ClientSessionStatistics.getRoute()} handle={{ crumb: "Sessions" }}
                   element={
                     <RequireRole userRoles={user.authorities} requiredRoles={[initSelectors.ROLES.ROLE_ADMIN, initSelectors.ROLES.ROLE_EXPERT_EN, initSelectors.ROLES.ROLE_EXPERT_RP]}>
                       <ClientSessionStatisticsPage />
                     </RequireRole>
                   }
            />
            <Route path={ClientApplicationSessionStatistics.getRoute()} handle={{ crumb: "Application Sessions" }}
                   element={
                     <RequireRole userRoles={user.authorities} requiredRoles={[initSelectors.ROLES.ROLE_ADMIN, initSelectors.ROLES.ROLE_EXPERT_EN, initSelectors.ROLES.ROLE_EXPERT_RP]}>
                       <ClientApplicationSessionStatisticsPage />
                     </RequireRole>
                   }
            />
            <Route path={ClientSynopticSessionsStatistics.getRoute()} handle={{ crumb: "Synoptics Sessions" }}
                   element={
                     <RequireRole userRoles={user.authorities} requiredRoles={[initSelectors.ROLES.ROLE_ADMIN, initSelectors.ROLES.ROLE_EXPERT_EN, initSelectors.ROLES.ROLE_EXPERT_RP]}>
                       <ClientSynopticSessionStatisticsPage />
                     </RequireRole>
                   }
            />
            <Route path={MSDAChannelStatistics.getRoute()} handle={{ crumb: "MSDA Channels" }} element={<MSDAChannelStatisticsPage />} />
            <Route path={LatestParametersStatistics.getRoute()} handle={{ crumb: "Latest Parameters" }} element={<LatestParametersStatisticsPage />} />
            <Route path={ParametersHistoryStatistics.getRoute()} handle={{ crumb: "Parameters History" }} element={<ParametersHistoryStatisticsPage />} />
          </Route>

          <Route path={Alarms.getRoute()} handle={{ crumb: "Alarms" }} element={<AlarmsPage />} />
          <Route path={MonitoringGroups.getRoute()} handle={{ crumb: "Monitoring Group" }} element={<MonitoringGroupsPage />} />
          <Route path={Trends.getRoute()} handle={{ crumb: "Trends" }} element={<TrendsPage />} />
          <Route index element={isApp ? <Navigate to={Trends.getRoute()} replace /> : <HomePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route index element={<Navigate to={isApp ? Trends.getLink({ platform: landingPlatform }, null, false) : PlatformRoot.getLink({ platform: platforms[0] })} replace />} />
      </Route>
  ));

  // Expose the data router so non-React callers (e.g. the redux async
  // middleware's post-save redirect) can navigate client-side instead of
  // triggering a full page reload.
  registerAppRouter(router);

  return (
      <RouterProvider router={router} />
  );
};

export default App;
